import { useEffect, useState } from 'react';
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, Users, Building2, Award, HelpCircle, Target, Eye, Image as ImageIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ImageUploadOrUrl from '../../components/ImageUploadOrUrl';

const TABS = [
  { id: 'hero',   label: 'Portada',       icon: ImageIcon },
  { id: 'intro',  label: 'Misión / Visión / Historia', icon: Target },
  { id: 'values', label: 'Valores',       icon: Sparkles },
  { id: 'directory', label: 'Directorio', icon: Users },
  { id: 'team',   label: 'Equipo',        icon: Users },
  { id: 'regionals', label: 'Regionales', icon: Building2 },
  { id: 'certifications', label: 'Certificaciones', icon: Award },
  { id: 'alliances', label: 'Alianzas',   icon: Award },
  { id: 'faqs',   label: 'FAQs',          icon: HelpCircle },
];

export default function AdminAbout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('hero');

  useEffect(() => {
    api.get('/admin/about').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const update = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero: data.hero,
        mission: data.mission,
        vision: data.vision,
        history: data.history,
        values: data.values,
        directory: data.directory,
        team: data.team,
        regionals: data.regionals,
        certifications: data.certifications,
        alliances: data.alliances,
        faqs: data.faqs,
      };
      const { data: fresh } = await api.patch('/admin/about', payload);
      setData(fresh);
      toast.success('Página "Nosotros" guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-cnib-blue" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-about">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Página pública</p>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Editor "Nosotros"</h1>
          <p className="mt-1 text-sm text-slate-500">Todo lo que cambies aquí aparece en /nosotros al instante.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/nosotros" target="_blank" rel="noopener noreferrer" className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="admin-about-preview">
            Ver página pública ↗
          </a>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-60" data-testid="admin-about-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar cambios
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-2xl border border-slate-200 bg-white p-1.5" data-testid="admin-about-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`admin-about-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-colors ${tab === t.id ? 'bg-cnib-navy text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {tab === 'hero' && <HeroEditor hero={data.hero || {}} onChange={(h) => update({ hero: h })} />}
        {tab === 'intro' && (
          <>
            <TextBlockEditor title="Misión" block={data.mission} onChange={(v) => update({ mission: v })} testidPrefix="about-mission" />
            <TextBlockEditor title="Visión" block={data.vision} onChange={(v) => update({ vision: v })} testidPrefix="about-vision" />
            <TextBlockEditor title="Historia" block={data.history} onChange={(v) => update({ history: v })} includeImage testidPrefix="about-history" />
          </>
        )}
        {tab === 'values' && (
          <ListEditor
            title="Pilares institucionales"
            items={data.values || []}
            onChange={(arr) => update({ values: arr })}
            empty={{ title: 'Nuevo valor', icon: 'Sparkles', text: '' }}
            testidPrefix="about-value"
            renderFields={(item, patch) => (
              <>
                <Field label="Título" value={item.title} onChange={(v) => patch({ title: v })} testid="value-title" />
                <Field label="Icono (ShieldCheck, GraduationCap, Sparkles, Landmark, Target, Eye, Award, Users)" value={item.icon} onChange={(v) => patch({ icon: v })} testid="value-icon" />
                <Textarea label="Descripción" value={item.text} onChange={(v) => patch({ text: v })} testid="value-text" />
              </>
            )}
          />
        )}
        {tab === 'directory' && (
          <ListEditor
            title="Consejo Directivo"
            items={data.directory || []}
            onChange={(arr) => update({ directory: arr })}
            empty={{ name: 'Nuevo miembro', role: '', bio: '', photo: '', email: '', linkedin: '' }}
            testidPrefix="about-directory"
            renderFields={(item, patch) => <MemberFields item={item} patch={patch} />}
          />
        )}
        {tab === 'team' && (
          <ListEditor
            title="Equipo operativo"
            items={data.team || []}
            onChange={(arr) => update({ team: arr })}
            empty={{ name: 'Nuevo integrante', role: '', bio: '', photo: '', email: '', linkedin: '' }}
            testidPrefix="about-team"
            renderFields={(item, patch) => <MemberFields item={item} patch={patch} />}
          />
        )}
        {tab === 'regionals' && (
          <ListEditor
            title="Regionales"
            items={data.regionals || []}
            onChange={(arr) => update({ regionals: arr })}
            empty={{ name: 'Nueva regional', city: '', address: '', phone: '', email: '', president: '', photo: '' }}
            testidPrefix="about-regional"
            renderFields={(item, patch) => (
              <>
                <Field label="Nombre regional" value={item.name} onChange={(v) => patch({ name: v })} testid="regional-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ciudad" value={item.city} onChange={(v) => patch({ city: v })} testid="regional-city" />
                  <Field label="Presidente" value={item.president} onChange={(v) => patch({ president: v })} testid="regional-president" />
                </div>
                <Field label="Dirección" value={item.address} onChange={(v) => patch({ address: v })} testid="regional-address" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Teléfono" value={item.phone} onChange={(v) => patch({ phone: v })} testid="regional-phone" />
                  <Field label="Email" value={item.email} onChange={(v) => patch({ email: v })} testid="regional-email" />
                </div>
                <ImageField label="Foto / portada" value={item.photo} onChange={(v) => patch({ photo: v })} testid="regional-photo" sizeHint="16:9 recomendado" />
              </>
            )}
          />
        )}
        {tab === 'certifications' && (
          <ListEditor
            title="Certificaciones y acreditaciones"
            items={data.certifications || []}
            onChange={(arr) => update({ certifications: arr })}
            empty={{ name: 'Nueva certificación', logo_url: '', description: '', link: '' }}
            testidPrefix="about-cert"
            renderFields={(item, patch) => (
              <>
                <Field label="Nombre" value={item.name} onChange={(v) => patch({ name: v })} testid="cert-name" />
                <Textarea label="Descripción corta" value={item.description} onChange={(v) => patch({ description: v })} testid="cert-desc" />
                <Field label="Link (opcional)" value={item.link} onChange={(v) => patch({ link: v })} testid="cert-link" />
                <ImageField label="Logo" value={item.logo_url} onChange={(v) => patch({ logo_url: v })} testid="cert-logo" sizeHint="PNG transparente, 240×240 px" />
              </>
            )}
          />
        )}
        {tab === 'alliances' && (
          <ListEditor
            title="Alianzas estratégicas"
            items={data.alliances || []}
            onChange={(arr) => update({ alliances: arr })}
            empty={{ name: 'Nueva alianza', logo_url: '', link: '' }}
            testidPrefix="about-ally"
            renderFields={(item, patch) => (
              <>
                <Field label="Nombre" value={item.name} onChange={(v) => patch({ name: v })} testid="ally-name" />
                <Field label="Link (opcional)" value={item.link} onChange={(v) => patch({ link: v })} testid="ally-link" />
                <ImageField label="Logo" value={item.logo_url} onChange={(v) => patch({ logo_url: v })} testid="ally-logo" sizeHint="PNG transparente" />
              </>
            )}
          />
        )}
        {tab === 'faqs' && (
          <ListEditor
            title="Preguntas frecuentes"
            items={data.faqs || []}
            onChange={(arr) => update({ faqs: arr })}
            empty={{ question: '¿Nueva pregunta?', answer: '' }}
            testidPrefix="about-faq"
            renderFields={(item, patch) => (
              <>
                <Field label="Pregunta" value={item.question} onChange={(v) => patch({ question: v })} testid="faq-q" />
                <Textarea label="Respuesta" value={item.answer} onChange={(v) => patch({ answer: v })} testid="faq-a" rows={4} />
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}

/* ---------- Sub-componentes reutilizables ---------- */

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15"
        data-testid={`admin-about-${testid}`}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 3, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <textarea
        rows={rows}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15"
        data-testid={`admin-about-${testid}`}
      />
    </label>
  );
}

function ImageField({ label, value, onChange, sizeHint, testid }) {
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <ImageUploadOrUrl value={value} onChange={onChange} sizeHint={sizeHint} testid={`admin-about-${testid}`} />
    </div>
  );
}

function HeroEditor({ hero, onChange }) {
  const patch = (p) => onChange({ ...hero, ...p });
  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="about-hero-editor">
      <h2 className="font-heading text-lg font-extrabold text-slate-900">Portada superior</h2>
      <Field label="Eyebrow (texto superior pequeño)" value={hero.eyebrow} onChange={(v) => patch({ eyebrow: v })} testid="hero-eyebrow" />
      <Field label="Título" value={hero.title} onChange={(v) => patch({ title: v })} testid="hero-title" />
      <Textarea label="Subtítulo" value={hero.subtitle} onChange={(v) => patch({ subtitle: v })} testid="hero-subtitle" />
      <ImageField label="Imagen de fondo (opcional)" value={hero.cover_image} onChange={(v) => patch({ cover_image: v })} testid="hero-cover" sizeHint="1920×600 recomendado, oscurece automáticamente" />
    </section>
  );
}

function TextBlockEditor({ title, block, onChange, includeImage = false, testidPrefix }) {
  const patch = (p) => onChange({ ...(block || {}), ...p });
  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`${testidPrefix}-editor`}>
      <h2 className="font-heading text-lg font-extrabold text-slate-900">{title}</h2>
      <Field label="Encabezado" value={block?.title} onChange={(v) => patch({ title: v })} testid={`${testidPrefix}-title`} />
      <Textarea label="Contenido" value={block?.body} onChange={(v) => patch({ body: v })} rows={6} testid={`${testidPrefix}-body`} />
      {includeImage && (
        <ImageField label="Imagen (opcional)" value={block?.image} onChange={(v) => patch({ image: v })} sizeHint="Formato libre" testid={`${testidPrefix}-image`} />
      )}
    </section>
  );
}

function MemberFields({ item, patch }) {
  return (
    <>
      <Field label="Nombre" value={item.name} onChange={(v) => patch({ name: v })} testid="member-name" />
      <Field label="Cargo" value={item.role} onChange={(v) => patch({ role: v })} testid="member-role" />
      <Textarea label="Bio" value={item.bio} onChange={(v) => patch({ bio: v })} testid="member-bio" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email" value={item.email} onChange={(v) => patch({ email: v })} testid="member-email" />
        <Field label="LinkedIn" value={item.linkedin} onChange={(v) => patch({ linkedin: v })} testid="member-linkedin" />
      </div>
      <ImageField label="Foto" value={item.photo} onChange={(v) => patch({ photo: v })} testid="member-photo" sizeHint="Vertical 4:5, 600×750 px" />
    </>
  );
}

function ListEditor({ title, items, onChange, renderFields, empty, testidPrefix }) {
  const add = () => {
    const next = [...items, { ...empty, order: items.length }];
    onChange(next);
  };
  const remove = (idx) => {
    if (!window.confirm('¿Eliminar este elemento?')) return;
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.map((it, i) => ({ ...it, order: i })));
  };
  const patchAt = (idx, p) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...p } : it));
    onChange(next);
  };

  return (
    <section className="space-y-3" data-testid={`${testidPrefix}-list`}>
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-extrabold text-slate-900">{title}</h2>
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-full bg-cnib-navy px-4 py-2 text-xs font-bold text-white hover:bg-cnib-blue" data-testid={`${testidPrefix}-add`}>
          <Plus className="h-3.5 w-3.5" /> Agregar
        </button>
      </div>
      {items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-500">
          Sin elementos. Hacé click en "Agregar" para crear el primero.
        </p>
      ) : (
        items.map((item, idx) => (
          <article key={idx} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`${testidPrefix}-item-${idx}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">#{idx + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded-full bg-slate-100 p-1.5 text-slate-500 disabled:opacity-30" data-testid={`${testidPrefix}-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => move(idx, +1)} disabled={idx === items.length - 1} className="rounded-full bg-slate-100 p-1.5 text-slate-500 disabled:opacity-30" data-testid={`${testidPrefix}-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => remove(idx)} className="rounded-full bg-red-50 p-1.5 text-red-600" data-testid={`${testidPrefix}-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="space-y-3">
              {renderFields(item, (p) => patchAt(idx, p))}
            </div>
          </article>
        ))
      )}
    </section>
  );
}
